import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from "recharts";
import { FileText } from "lucide-react";
import { getProposalStatus } from "@/constants/proposalMeta";
import { calculateWeightedValue } from "@/utils/proposalUtils";

type ViewMode = "number" | "value";

const ProposalsChartSkeleton = () => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Skeleton className="h-5 w-5" />
        <Skeleton className="h-6 w-48" />
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="h-80 flex items-center justify-center">
        <Skeleton className="h-full w-full" />
      </div>
    </CardContent>
  </Card>
);

interface ProposalInfo {
  clientName: string;
  title: string;
  value: number;
  weightedValue: number;
}

interface MonthlyGroup {
  pending: ProposalInfo[];
  accepted: ProposalInfo[];
  rejected: ProposalInfo[];
  cancelled: ProposalInfo[];
}

interface SummaryData {
  pending: { count: number; value: number };
  accepted: { count: number; value: number };
  rejected: { count: number; value: number };
  cancelled: { count: number; value: number };
}

function processProposals(data: any[], year: number) {
  const summaryData: SummaryData = {
    pending: { count: 0, value: 0 },
    accepted: { count: 0, value: 0 },
    rejected: { count: 0, value: 0 },
    cancelled: { count: 0, value: 0 },
  };

  const monthlyData: { [key: string]: MonthlyGroup } = {};

  data.forEach((proposal) => {
    if (!proposal.sent_date) return;
    const date = new Date(proposal.sent_date);
    const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

    if (!monthlyData[monthYear]) {
      monthlyData[monthYear] = { pending: [], accepted: [], rejected: [], cancelled: [] };
    }

    const actualStatus = getProposalStatus(proposal);
    const clientName = (proposal as any).clients?.name || "Unknown Client";
    const proposalValue = Number(proposal.final_value || proposal.value || 0);
    const weightedValue = calculateWeightedValue(proposalValue, proposal.probability);

    const proposalData: ProposalInfo = { clientName, title: proposal.title, value: proposalValue, weightedValue };

    if (actualStatus === "accepted") {
      monthlyData[monthYear].accepted.push(proposalData);
      summaryData.accepted.count++;
      summaryData.accepted.value += proposalValue;
    } else if (actualStatus === "rejected") {
      monthlyData[monthYear].rejected.push(proposalData);
      summaryData.rejected.count++;
      summaryData.rejected.value += proposalValue;
    } else if (actualStatus === "cancelled") {
      monthlyData[monthYear].cancelled.push(proposalData);
      summaryData.cancelled.count++;
      summaryData.cancelled.value += proposalValue;
    } else {
      monthlyData[monthYear].pending.push(proposalData);
      summaryData.pending.count++;
      summaryData.pending.value += proposalValue;
    }
  });

  const allMonths: string[] = [];
  for (let month = 1; month <= 12; month++) {
    allMonths.push(`${year}-${String(month).padStart(2, "0")}`);
  }

  const chartData = allMonths.map((monthYear) => {
    const [y, m] = monthYear.split("-");
    const monthName = new Date(parseInt(y), parseInt(m) - 1).toLocaleDateString("el-GR", {
      month: "short",
      year: "2-digit",
    });

    const d = monthlyData[monthYear] || { pending: [], accepted: [], rejected: [], cancelled: [] };

    return {
      month: monthName,
      pending: d.pending.length,
      accepted: d.accepted.length,
      rejected: d.rejected.length,
      cancelled: d.cancelled.length,
      pendingValue: d.pending.reduce((s, p) => s + p.value, 0),
      acceptedValue: d.accepted.reduce((s, p) => s + p.value, 0),
      rejectedValue: d.rejected.reduce((s, p) => s + p.value, 0),
      cancelledValue: d.cancelled.reduce((s, p) => s + p.value, 0),
      total: d.pending.length + d.accepted.length + d.rejected.length + d.cancelled.length,
      totalValue:
        d.pending.reduce((s, p) => s + p.value, 0) +
        d.accepted.reduce((s, p) => s + p.value, 0) +
        d.rejected.reduce((s, p) => s + p.value, 0) +
        d.cancelled.reduce((s, p) => s + p.value, 0),
      fullDate: monthYear,
      proposalsData: d,
    };
  });

  return { chartData, summaryData };
}

export function ProposalsChart() {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [viewMode, setViewMode] = useState<ViewMode>("number");
  const yearOptions = [2025, 2026, 2027];

  // Fetch selected year data
  const { data: currentYearResult, isLoading: isLoadingCurrent, error } = useQuery({
    queryKey: ["proposals-chart-v5", selectedYear],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("proposals")
        .select("sent_date, status, title, value, final_value, probability, response_deadline, client_id, clients(name)")
        .not("sent_date", "is", null)
        .gte("sent_date", `${selectedYear}-01-01`)
        .lte("sent_date", `${selectedYear}-12-31`)
        .order("sent_date", { ascending: true });

      if (error) throw error;
      if (!data || data.length === 0) {
        return { chartData: [], summaryData: { pending: { count: 0, value: 0 }, accepted: { count: 0, value: 0 }, rejected: { count: 0, value: 0 }, cancelled: { count: 0, value: 0 } } };
      }
      return processProposals(data, selectedYear);
    },
    staleTime: 0,
  });

  // Fetch previous year data
  const prevYear = selectedYear - 1;
  const { data: prevYearResult } = useQuery({
    queryKey: ["proposals-chart-v5", prevYear],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("proposals")
        .select("sent_date, status, title, value, final_value, probability, response_deadline, client_id, clients(name)")
        .not("sent_date", "is", null)
        .gte("sent_date", `${prevYear}-01-01`)
        .lte("sent_date", `${prevYear}-12-31`)
        .order("sent_date", { ascending: true });

      if (error) throw error;
      if (!data || data.length === 0) {
        return { chartData: [], summaryData: { pending: { count: 0, value: 0 }, accepted: { count: 0, value: 0 }, rejected: { count: 0, value: 0 }, cancelled: { count: 0, value: 0 } } };
      }
      return processProposals(data, prevYear);
    },
    staleTime: 0,
  });

  const chartDataCurrent = currentYearResult?.chartData || [];
  const chartDataPrev = prevYearResult?.chartData || [];
  const summaryData = currentYearResult?.summaryData || { pending: { count: 0, value: 0 }, accepted: { count: 0, value: 0 }, rejected: { count: 0, value: 0 }, cancelled: { count: 0, value: 0 } };

  // Merge current and previous year data
  const mergedChartData = chartDataCurrent.map((item, index) => {
    const prev = chartDataPrev[index];
    return {
      ...item,
      prevPending: prev?.pending || 0,
      prevAccepted: prev?.accepted || 0,
      prevRejected: prev?.rejected || 0,
      prevCancelled: prev?.cancelled || 0,
      prevPendingValue: prev?.pendingValue || 0,
      prevAcceptedValue: prev?.acceptedValue || 0,
      prevRejectedValue: prev?.rejectedValue || 0,
      prevCancelledValue: prev?.cancelledValue || 0,
      prevTotal: prev?.total || 0,
      prevTotalValue: prev?.totalValue || 0,
    };
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("el-GR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCompactCurrency = (amount: number) => {
    if (amount >= 1000) {
      return new Intl.NumberFormat("el-GR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
        notation: "compact",
      }).format(amount);
    }
    return formatCurrency(amount);
  };

  if (isLoadingCurrent) {
    return <ProposalsChartSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Proposals per Month
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80 flex items-center justify-center text-muted-foreground">
            Failed to load proposals chart data: {(error as Error).message}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Determine what to show as primary/secondary in summary cards based on viewMode
  const renderSummaryCard = (label: string, colorClass: string, data: { count: number; value: number }) => {
    const primary = viewMode === "number" ? data.count : formatCurrency(data.value);
    const secondary = viewMode === "number" ? formatCurrency(data.value) : `${data.count} proposal${data.count !== 1 ? "s" : ""}`;

    return (
      <div className="text-center p-3 bg-muted/30 rounded-lg">
        <div className={`text-2xl font-bold ${colorClass}`}>{primary}</div>
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className="text-sm font-medium text-foreground">{secondary}</div>
      </div>
    );
  };

  // Chart data keys based on view mode
  const pendingKey = viewMode === "number" ? "pending" : "pendingValue";
  const acceptedKey = viewMode === "number" ? "accepted" : "acceptedValue";
  const rejectedKey = viewMode === "number" ? "rejected" : "rejectedValue";
  const cancelledKey = viewMode === "number" ? "cancelled" : "cancelledValue";
  const totalKey = viewMode === "number" ? "total" : "totalValue";
  const prevPendingKey = viewMode === "number" ? "prevPending" : "prevPendingValue";
  const prevAcceptedKey = viewMode === "number" ? "prevAccepted" : "prevAcceptedValue";
  const prevRejectedKey = viewMode === "number" ? "prevRejected" : "prevRejectedValue";
  const prevCancelledKey = viewMode === "number" ? "prevCancelled" : "prevCancelledValue";
  const prevTotalKey = viewMode === "number" ? "prevTotal" : "prevTotalValue";

  const renderBarLabel = (props: any, dataKey: string) => {
    const { x, y, width, height, value } = props;
    if (!value || value === 0) return null;
    const centerX = Number(x) + Number(width) / 2;
    const centerY = Number(y) + Number(height) / 2;
    const displayValue = viewMode === "value" ? formatCompactCurrency(value) : value;
    return (
      <text x={centerX} y={centerY} fill="white" textAnchor="middle" dominantBaseline="central" fontSize="10" fontWeight="600">
        {displayValue}
      </text>
    );
  };

  const renderTotalLabel = (props: any, key: string) => {
    const { x, y, width, index } = props;
    const item = mergedChartData[index];
    if (!item) return null;
    const totalVal = (item as any)[key];
    if (!totalVal || totalVal === 0) return null;
    const centerX = Number(x) + Number(width) / 2;
    const topY = Number(y) - 8;
    const displayValue = viewMode === "value" ? formatCompactCurrency(totalVal) : totalVal;
    return (
      <text x={centerX} y={topY} fill="hsl(var(--foreground))" textAnchor="middle" dominantBaseline="bottom" fontSize="11" fontWeight="bold">
        {displayValue}
      </text>
    );
  };

  return (
    <Card className="animate-fade-in">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Proposals per Month</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
              <SelectTrigger className="w-[110px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background">
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="value">Value</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
              <SelectTrigger className="w-[100px]">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent className="bg-background">
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 pt-4">
          {renderSummaryCard("Pending", "text-info", summaryData.pending)}
          {renderSummaryCard("Accepted", "text-success", summaryData.accepted)}
          {renderSummaryCard("Rejected", "text-destructive", summaryData.rejected)}
          {renderSummaryCard("Cancelled", "text-warning", summaryData.cancelled)}
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={mergedChartData} margin={{ top: 40, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis
                fontSize={12}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                tickFormatter={viewMode === "value" ? (v) => formatCompactCurrency(v) : undefined}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const tooltipData = payload[0]?.payload;
                  const proposalsData = tooltipData?.proposalsData;

                  const statusColors = {
                    pending: "hsl(var(--info))",
                    accepted: "hsl(var(--success))",
                    rejected: "hsl(var(--destructive))",
                    cancelled: "hsl(var(--warning))",
                  };

                  const englishMonth = tooltipData?.fullDate
                    ? new Date(tooltipData.fullDate + "-01").toLocaleDateString("en-US", { month: "short" })
                    : "";

                  const allProposals = [
                    ...(proposalsData?.pending?.map((p: any) => ({ ...p, status: "pending" })) || []),
                    ...(proposalsData?.accepted?.map((p: any) => ({ ...p, status: "accepted" })) || []),
                    ...(proposalsData?.rejected?.map((p: any) => ({ ...p, status: "rejected" })) || []),
                    ...(proposalsData?.cancelled?.map((p: any) => ({ ...p, status: "cancelled" })) || []),
                  ];

                  return (
                    <div className="rounded-lg border bg-background p-2 shadow-lg" style={{ width: "fit-content", minWidth: "280px", maxWidth: "320px", maxHeight: "300px", overflowY: "auto" }}>
                      <div className="mb-2">
                        <div className="text-xs font-bold text-foreground">
                          {englishMonth} • {allProposals.length} proposal{allProposals.length !== 1 ? "s" : ""}
                        </div>
                      </div>
                      {allProposals.length > 0 ? (
                        <div className="space-y-1">
                          {allProposals.map((proposal: any, index: number) => (
                            <div key={index} className="flex items-center gap-2 py-0.5">
                              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: statusColors[proposal.status as keyof typeof statusColors] }} />
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-foreground truncate" title={proposal.clientName}>
                                  {proposal.clientName}
                                </div>
                              </div>
                              <div className="text-xs font-bold flex-shrink-0" style={{ color: statusColors[proposal.status as keyof typeof statusColors] }}>
                                {formatCurrency(proposal.weightedValue)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground">No proposals this month</div>
                      )}
                    </div>
                  );
                }}
              />

              {/* Current year bars (solid, wider) */}
              <Bar dataKey={pendingKey} stackId="current" fill="hsl(var(--info))" radius={[0, 0, 0, 0]} barSize={40}>
                <LabelList dataKey={pendingKey} position="center" content={(props: any) => renderBarLabel(props, pendingKey)} />
              </Bar>
              <Bar dataKey={acceptedKey} stackId="current" fill="hsl(var(--success))" radius={[0, 0, 0, 0]} barSize={40}>
                <LabelList dataKey={acceptedKey} position="center" content={(props: any) => renderBarLabel(props, acceptedKey)} />
              </Bar>
              <Bar dataKey={rejectedKey} stackId="current" fill="hsl(var(--destructive))" radius={[0, 0, 0, 0]} barSize={40}>
                <LabelList dataKey={rejectedKey} position="center" content={(props: any) => renderBarLabel(props, rejectedKey)} />
              </Bar>
              <Bar dataKey={cancelledKey} stackId="current" fill="hsl(var(--warning))" radius={[4, 4, 0, 0]} barSize={40}>
                <LabelList dataKey={cancelledKey} position="center" content={(props: any) => renderBarLabel(props, cancelledKey)} />
                <LabelList dataKey={totalKey} position="top" content={(props: any) => renderTotalLabel(props, totalKey)} />
              </Bar>

              {/* Previous year bars (faded, narrower) */}
              <Bar dataKey={prevPendingKey} stackId="prev" fill="hsl(var(--info))" opacity={0.3} radius={[0, 0, 0, 0]} barSize={20} />
              <Bar dataKey={prevAcceptedKey} stackId="prev" fill="hsl(var(--success))" opacity={0.3} radius={[0, 0, 0, 0]} barSize={20} />
              <Bar dataKey={prevRejectedKey} stackId="prev" fill="hsl(var(--destructive))" opacity={0.3} radius={[0, 0, 0, 0]} barSize={20} />
              <Bar dataKey={prevCancelledKey} stackId="prev" fill="hsl(var(--warning))" opacity={0.3} radius={[4, 4, 0, 0]} barSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
